import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { ApiError, backendApi, type AuthUser } from '@/lib/backendApi';
import { clearAuthTransitionPending, markAuthTransitionPending } from '@/lib/authTransition';
import { clearAuthFlowTrace, startAuthFlowTrace, traceAuth } from '@/lib/authTrace';

interface AuthActionResult {
  error: Error | null;
  emailConfirmationRequired?: boolean;
  reason?: 'email_not_confirmed' | 'invalid_credentials' | 'unknown';
}

type SessionState = { authenticated: true } | null;

interface AuthContextType {
  user: AuthUser | null;
  session: SessionState;
  isAdmin: boolean;
  isCheckingAdmin: boolean;
  loading: boolean;
  isPasswordRecovery: boolean;
  isRecoveryExpired: boolean;
  isRecoverySessionReady: boolean;
  isRecoverySessionLoading: boolean;
  refreshSession: () => Promise<AuthUser | null>;
  setPasswordRecovery: (open: boolean) => void;
  signUp: (email: string, password: string, fullName?: string) => Promise<AuthActionResult>;
  signIn: (email: string, password: string) => Promise<AuthActionResult>;
  signInWithGoogle: () => Promise<AuthActionResult>;
  sendPasswordReset: (email: string) => Promise<AuthActionResult>;
  resendEmailConfirmation: (email: string) => Promise<AuthActionResult>;
  verifyEmailOtp: (email: string, token: string) => Promise<AuthActionResult & { authenticated?: boolean }>;
  signOut: () => Promise<void>;
  updatePassword: (newPassword: string, options?: { silent?: boolean }) => Promise<AuthActionResult>;
  deleteAccount: () => Promise<AuthActionResult>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<SessionState>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isPasswordRecovery, setPasswordRecovery] = useState(false);
  const [isRecoveryExpired, setRecoveryExpired] = useState(false);
  const [isRecoverySessionReady, setRecoverySessionReady] = useState(false);
  const [isRecoverySessionLoading, setRecoverySessionLoading] = useState(false);
  const { t } = useLanguage();
  const { actualTheme } = useTheme();

  const getRedirectUrl = () => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const envRedirect = (import.meta as ImportMeta).env?.VITE_AUTH_REDIRECT_URL;
    if (envRedirect) {
      return envRedirect;
    }

    return `${window.location.origin}/`;
  };

  const refreshSession = useCallback(async (): Promise<AuthUser | null> => {
    const startedAt = performance.now();
    traceAuth('auth.refreshSession.start', { path: typeof window !== 'undefined' ? window.location.pathname : null });
    try {
      const { user: me, isAdmin: adminAccess } = await backendApi.sessionContext();
      setUser(me);
      setSession(me ? { authenticated: true } : null);
      setIsAdmin(Boolean(me && adminAccess));
      if (me) {
        clearAuthTransitionPending();
      }
      traceAuth('auth.refreshSession.success', {
        hasUser: Boolean(me),
        isAdmin: Boolean(me && adminAccess),
        elapsedMs: Math.round(performance.now() - startedAt)
      });
      return me;
    } catch {
      setUser(null);
      setSession(null);
      setIsAdmin(false);
      traceAuth('auth.refreshSession.error', {
        elapsedMs: Math.round(performance.now() - startedAt)
      });
      return null;
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const currentUrl = new URL(window.location.href);
    const code = currentUrl.searchParams.get('code');
    const isInFunctionCallback = currentUrl.pathname.startsWith('/functions/oauth-callback');

    // Fallback: if OAuth returns to the app root with ?code=..., forward it to
    // the edge callback that exchanges the code and sets httpOnly cookies.
    if (code && !isInFunctionCallback) {
      const next = `${currentUrl.origin}${currentUrl.pathname}`;
      const callbackUrl = new URL(`${currentUrl.origin}/functions/oauth-callback`);
      callbackUrl.searchParams.set('code', code);
      callbackUrl.searchParams.set('next', next);
      window.location.replace(callbackUrl.toString());
      return;
    }

    const search = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const type = hash.get('type') || search.get('type');

    if (type === 'recovery') {
      setPasswordRecovery(true);
      setRecoveryExpired(false);
      setRecoverySessionReady(false);

      const errorCode = hash.get('error_code') || search.get('error_code');
      const error = hash.get('error') || search.get('error');

      const accessToken = hash.get('access_token');
      const refreshToken = hash.get('refresh_token');

      if (error || errorCode) {
        setRecoveryExpired(true);
      } else if (accessToken && refreshToken) {
        setRecoverySessionLoading(true);
        backendApi
          .setRecoverySession(accessToken, refreshToken)
          .then(() => {
            setRecoverySessionReady(true);
            setRecoveryExpired(false);
          })
          .catch(() => {
            setRecoveryExpired(true);
          })
          .finally(() => {
            setRecoverySessionLoading(false);
          });
      } else {
        setRecoveryExpired(true);
      }

      if (window.history?.replaceState) {
        search.delete('type');
        window.history.replaceState(
          {},
          document.title,
          `${window.location.pathname}${search.toString() ? `?${search.toString()}` : ''}`
        );
      }
    }
  }, []);

  useEffect(() => {
    traceAuth('auth.state.changed', {
      loading,
      hasUser: Boolean(user),
      isAuthenticated: Boolean(session),
      isPasswordRecovery
    });
  }, [isPasswordRecovery, loading, session, user]);

  useEffect(() => {
    let isMounted = true;

    const bootstrap = async () => {
      try {
        const me = await refreshSession();
        if (!isMounted) return;
        if (!me) {
          setIsAdmin(false);
        }
      } catch (error) {
        if (!isMounted) return;
        setUser(null);
        setSession(null);
        setIsAdmin(false);
      } finally {
        if (isMounted) {
          setIsCheckingAdmin(false);
          setLoading(false);
        }
      }
    };

    void bootstrap();

    return () => {
      isMounted = false;
    };
  }, [refreshSession]);

  const signUp = async (email: string, password: string, fullName?: string) => {
    try {
      const response = await backendApi.signUp(email, password, fullName, getRedirectUrl());
      const emailConfirmationRequired = Boolean(response.emailConfirmationRequired);

      if (response.user) {
        setUser(response.user);
        setSession({ authenticated: true });
        setIsAdmin(Boolean(response.isAdmin));
      } else {
        setUser(null);
        setSession(null);
        setIsAdmin(false);
      }

      return { error: null, emailConfirmationRequired };
    } catch (error) {
      toast.error(t('auth.errorSignUp'));
      return { error: error as Error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { user: loggedUser, isAdmin: adminAccess } = await backendApi.login(email, password);
      setUser(loggedUser);
      setSession({ authenticated: true });
      setIsAdmin(Boolean(adminAccess));
      setPasswordRecovery(false);
      setRecoveryExpired(false);
      setRecoverySessionReady(false);
      setRecoverySessionLoading(false);
      clearAuthTransitionPending();
      return { error: null };
    } catch (error) {
      const message = String((error as Error)?.message || '').toLowerCase();
      const status = error instanceof ApiError ? error.status : null;

      if (status === 403 || message.includes('email not confirmed')) {
        toast.error(t('auth.errorEmailNotConfirmed'));
        return { error: error as Error, reason: 'email_not_confirmed' };
      } else if (status === 401 || message.includes('invalid credentials')) {
        toast.error(t('auth.errorInvalidCredentials'));
        return { error: error as Error, reason: 'invalid_credentials' };
      } else {
        toast.error(t('auth.error'));
        return { error: error as Error, reason: 'unknown' };
      }
    }
  };

  const sendPasswordReset = async (email: string) => {
    try {
      await backendApi.sendPasswordReset(email, getRedirectUrl());
      const isDark = actualTheme === 'dark';
      const toastId = toast.success('', {
        description: t('auth.resetPasswordSentDescription'),
        duration: Infinity,
        classNames: {
          description: isDark ? 'text-foreground' : undefined,
          actionButton: isDark ? '!bg-transparent !text-white' : '!bg-transparent !text-black'
        },
        action: {
          label: '✕',
          onClick: () => toast.dismiss(toastId)
        },
        actionButtonStyle: {
          background: 'transparent',
          color: isDark ? '#fff' : '#000',
          paddingLeft: '6px',
          paddingRight: '6px'
        }
      });

      return { error: null };
    } catch (error) {
      toast.error(t('auth.resetPasswordError'));
      return { error: error as Error };
    }
  };

  const resendEmailConfirmation = async (email: string) => {
    try {
      await backendApi.resendConfirmation(email, getRedirectUrl());
      toast.success(t('auth.confirmationResent'));
      return { error: null };
    } catch (error) {
      toast.error(t('auth.confirmationResendError'));
      return { error: error as Error };
    }
  };

  const verifyEmailOtp = async (email: string, token: string) => {
    try {
      const response = await backendApi.verifyEmailOtp(email, token);
      const authenticated = Boolean(response.user);

      if (response.user) {
        setUser(response.user);
        setSession({ authenticated: true });
        setIsAdmin(Boolean(response.isAdmin));
        clearAuthTransitionPending();
      }

      if (!response.user) {
        setUser(null);
        setSession(null);
        setIsAdmin(false);
      }

      return { error: null, authenticated };
    } catch (error) {
      toast.error(t('auth.confirmationCodeError'));
      return { error: error as Error, authenticated: false };
    }
  };

  const signInWithGoogle = async () => {
    if (typeof window === 'undefined') {
      return { error: new Error('Window is not available') };
    }

    try {
      markAuthTransitionPending();
      startAuthFlowTrace('auth.google.click', {
        path: window.location.pathname,
        search: window.location.search
      });
      const startedAt = performance.now();
      const target = `${window.location.pathname}${window.location.search}`;
      const next = `${window.location.origin}/auth/processing?target=${encodeURIComponent(target)}`;
      const { url } = await backendApi.startGoogleLogin(next);
      traceAuth('auth.google.redirect_ready', {
        elapsedMs: Math.round(performance.now() - startedAt),
        next
      });
      window.location.assign(url);
      return { error: null };
    } catch (error) {
      clearAuthTransitionPending();
      traceAuth('auth.google.error', {
        message: (error as Error)?.message ?? 'unknown_error'
      });
      toast.error(t('auth.error'));
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    try {
      await backendApi.logout();
    } catch (error) {
      console.warn('Unable to clear server session', error);
    }

    setSession(null);
    setUser(null);
    setIsAdmin(false);
    setLoading(false);
    setPasswordRecovery(false);
    setRecoveryExpired(false);
    clearAuthTransitionPending();
    clearAuthFlowTrace();
  };

  const updatePassword = async (newPassword: string, options?: { silent?: boolean }) => {
    try {
      await backendApi.updatePassword(newPassword);
      return { error: null };
    } catch (error) {
      if (!options?.silent) {
        const message = String((error as Error)?.message || '').toLowerCase();
        const isReuse =
          message.includes('same password') ||
          message.includes('different from old') ||
          message.includes('new password should be different');

        toast.error(isReuse ? t('auth.passwordReuseError') : t('auth.errorUpdatePassword'));
      }

      return { error: error as Error };
    }
  };

  const deleteAccount = async () => {
    if (!user) {
      const error = new Error('User not found');
      toast.error(t('auth.errorDeleteAccount'));
      return { error };
    }

    try {
      await backendApi.deleteAccount();
      setSession(null);
      setUser(null);
      setIsAdmin(false);
      clearAuthFlowTrace();
      toast.success(t('auth.deleteAccountSuccess'));
      return { error: null };
    } catch (error) {
      console.error('Failed to delete user account', error);
      toast.error(t('auth.errorDeleteAccount'));
      return { error: error as Error };
    }
  };

  const value: AuthContextType = {
    user,
    session,
    isAdmin,
    isCheckingAdmin,
    loading,
    isPasswordRecovery,
    isRecoveryExpired,
    isRecoverySessionReady,
    isRecoverySessionLoading,
    refreshSession,
    setPasswordRecovery,
    signUp,
    signIn,
    signInWithGoogle,
    sendPasswordReset,
    resendEmailConfirmation,
    verifyEmailOtp,
    signOut,
    updatePassword,
    deleteAccount
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
